import { useCallback, useEffect, useRef, useState } from "react";
import { OrganizationalRecordPerson } from "./OrganizationalRecordPerson";
import { FormattedMessage, useIntl } from "react-intl";
import { useApolloClient } from "@apollo/client/react";
import { Button } from "@/components/design/Button";
import { Dialog } from "@/components/common/Dialog";
import { DocumentLibrary } from "@/components/documents/DocumentLibrary";
import type { DocumentItem } from "@/components/documents/types";
import {
  CreateOrganizationalRecordDocumentDocument, DeleteOrganizationalRecordDocumentDocument, OrganizationalRecordDocumentContentDocument,
  OrganizationalRecordDocumentPreviewDocument, OrganizationalRecordDocumentsDocument, UpdateOrganizationalRecordDocumentDocument,
  type OrganizationalRecordDocumentsQuery,
} from "@/graphql/generated/operations";
import { ingestFiles } from "@/lib/file-ingest";
import { downloadBlob } from "@/lib/file-download";
import { base64ToBlob } from "@/lib/use-open-document";
import { MAX_DOCUMENT_UPLOAD_BYTES, PROJECT_DOCUMENT_ACCEPT } from "@/lib/upload-limits";
import { userErrorMessage } from "@/lib/user-error";
import { LocalizedError } from "@/lib/localized-error";
import { OrganizationalRecordItemKind } from "@/graphql/generated/operations";
import type { OrganizationalRecordNavigate } from "./OrganizationalRecordLinks";

type Document = OrganizationalRecordDocumentsQuery["organizationalRecordDocuments"]["nodes"][number];
const previewTypes = new Set(["application/pdf", "image/png", "image/jpeg", "image/gif", "image/webp", "text/plain"]);
const thumbnailTypes = new Set(["image/png", "image/jpeg", "image/gif", "image/webp"]);

/** Organizational adapter: no personal Document queries, normalized cache or assistant context. */
export function OrganizationalRecordDocuments({ recordId, canWrite, onNavigate, embedded = false }: { recordId: string; canWrite: boolean; onNavigate?: OrganizationalRecordNavigate; embedded?: boolean }) {
  const intl = useIntl();
  const client = useApolloClient();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [writeError, setWriteError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ url: string; title: string } | null>(null);
  const revision = useRef(0);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview.url); }, [preview]);

  // Search/filter/count use the complete metadata set. Each request is bounded; thumbnails and
  // original bytes are fetched separately, only for the displayed preview cards or an explicit open.
  const reload = useCallback(async () => {
    const current = ++revision.current;
    setLoading(true); setListError(null);
    try {
      const items: Document[] = [];
      const seen = new Set<string>();
      let after: string | undefined;
      while (true) {
        const { data } = await client.query({ query: OrganizationalRecordDocumentsDocument,
          variables: { input: { recordId, first: 100, ...(after ? { after } : {}) } }, fetchPolicy: "no-cache" });
        if (current !== revision.current) return;
        if (!data) throw new Error("Missing organizationalRecord documents");
        const page = data.organizationalRecordDocuments;
        items.push(...page.nodes);
        if (!page.pageInfo.hasNextPage) break;
        const cursor = page.pageInfo.endCursor;
        if (!cursor || seen.has(cursor)) throw new Error("OrganizationalRecord document cursor did not advance");
        seen.add(cursor); after = cursor;
      }
      setDocuments(Array.from(new Map(items.map(item => [item.id, item])).values()));
    } catch (failure) {
      if (current !== revision.current) return;
      setDocuments([]); setListError(userErrorMessage(intl, failure));
      throw failure;
    } finally { if (current === revision.current) setLoading(false); }
  }, [client, recordId, intl]);
  const cancelLoads = useCallback(() => { revision.current++; }, []);
  useEffect(() => {
    let active = true;
    queueMicrotask(() => { if (active) void reload().catch(() => undefined); });
    return () => { active = false; cancelLoads(); };
  }, [reload, cancelLoads]);

  const loadPreview = useCallback(async (id: string) => {
    const { data } = await client.query({ query: OrganizationalRecordDocumentPreviewDocument, variables: { id }, fetchPolicy: "no-cache" });
    const file = data?.organizationalRecordDocument?.preview;
    return file?.content && file.mimeType && thumbnailTypes.has(file.mimeType)
      ? `data:${file.mimeType};base64,${file.content}` : null;
  }, [client]);

  async function open(id: string, download = false) {
    const document = documents.find(item => item.id === id);
    if (!document) return;
    const { data } = await client.query({ query: OrganizationalRecordDocumentContentDocument, variables: { id }, fetchPolicy: "no-cache" });
    const file = data?.organizationalRecordDocument?.content;
    if (!file?.content) throw new LocalizedError(intl.formatMessage({ id: "service.page.errNoContent" }));
    const mime = file.mimeType ?? "application/octet-stream";
    const blob = base64ToBlob(file.content, mime);
    if (download || !previewTypes.has(mime)) downloadBlob(blob, document.fileName);
    else setPreview({ url: URL.createObjectURL(blob), title: document.title });
  }

  async function upload(files: FileList | File[]) {
    if (!canWrite) return;
    setWriteError(null);
    const result = await ingestFiles(Array.from(files), { maxBytes: MAX_DOCUMENT_UPLOAD_BYTES, accept: PROJECT_DOCUMENT_ACCEPT });
    if (result.rejected.length || result.accepted.some(file => file.size === 0)) {
      throw new LocalizedError(intl.formatMessage({ id: "organizationalRecord.uploadRejected" }));
    }
    try {
      for (const file of result.accepted) await client.mutate({ mutation: CreateOrganizationalRecordDocumentDocument, fetchPolicy: "no-cache",
        variables: { input: { recordId, title: file.name, fileName: file.name, content: { content: file.content, mimeType: file.mimeType } } },
      });
    } finally { await reload(); }
  }

  async function rename(id: string, title: string) {
    if (!canWrite) throw new LocalizedError(intl.formatMessage({ id: "organizationalRecord.noAccess" }));
    setWriteError(null);
    const { data } = await client.mutate({ mutation: UpdateOrganizationalRecordDocumentDocument, variables: { id, title }, fetchPolicy: "no-cache" });
    if (!data) throw new Error("Document rename was not confirmed");
    setDocuments(previous => previous.map(item => item.id === id ? { ...item, title: data.updateOrganizationalRecordDocument.title } : item));
  }

  async function remove(ids: readonly string[]) {
    if (!canWrite) throw new LocalizedError(intl.formatMessage({ id: "organizationalRecord.noAccess" }));
    const failed: string[] = [];
    setWriteError(null);
    for (const id of ids) {
      try {
        await client.mutate({ mutation: DeleteOrganizationalRecordDocumentDocument, variables: { id }, fetchPolicy: "no-cache" });
        setDocuments(previous => previous.filter(item => item.id !== id));
      } catch (failure) { failed.push(id); setWriteError(userErrorMessage(intl, failure)); }
    }
    return failed;
  }

  const items: DocumentItem[] = documents.map(document => {
    const kind = document.fileName.match(/\.([a-z0-9]{2,8})$/i)?.[1]?.toUpperCase() ?? "DOC";
    return { id: document.id, name: document.title, kind, fileType: kind, when: document.createdAt,
      source: document.createdBy?.formattedName ?? "–", sourceDisplay: document.createdBy ? <OrganizationalRecordPerson person={document.createdBy} /> : undefined,
      groupId: null, preview: null, canDownload: true };
  });
  return <>
    <DocumentLibrary scopeId={`organizationalRecord-documents:${recordId}`} items={items} embedded={embedded}
      title={<FormattedMessage id="organizationalRecord.documents" />} description={<FormattedMessage id="organizationalRecord.documentsHint" />}
      loading={loading} accept={PROJECT_DOCUMENT_ACCEPT} loadPreview={loadPreview}
      onOpen={id => onNavigate ? onNavigate({ id, kind: OrganizationalRecordItemKind.Document, title: documents.find(item => item.id === id)?.title ?? "" }) : open(id)} onDownload={id => open(id, true)}
      onUpload={canWrite ? upload : undefined} onRename={canWrite ? rename : undefined} onDelete={canWrite ? remove : undefined}
      error={listError || writeError ? <>
        {(listError || writeError) && <p role="alert" className="mt-3 field-meta text-ink/70">{listError ?? writeError}</p>}
        {listError && <Button variant="secondary" size="sm" className="mt-2" onClick={() => void reload().catch(() => undefined)}>
          <FormattedMessage id="common.tryAgain" defaultMessage="Try again" /></Button>}
      </> : null}
    />
    <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} title={preview?.title} maxWidth="lg">
      {preview && <iframe title={preview.title} src={preview.url} sandbox="" className="h-[65vh] w-full border-0" />}
    </Dialog>
  </>;
}
